"use client";

import { RiCalendarEventLine, RiCloseLine, RiDoorClosedLine, RiMapPinLine, RiSunLine, RiTeamLine, RiTentLine } from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@workspace/ui/components/dialog";
import Link from "next/link";
import { useState } from "react";

interface VenueDetailModalProps {
	isOpen: boolean;
	venue: {
		id: string;
		name: string;
		slug?: string;
		capacity?: number | null;
		venue_type?: string | null;
		address?: string | null;
		city?: string | null;
		state?: string | null;
		country?: string | null;
	};
	onClose: () => void;
	showUpcomingShowsButton?: boolean;
	initialView?: "info" | "map";
}

export function VenueDetailModal({
	isOpen,
	venue,
	onClose,
	showUpcomingShowsButton = true,
	initialView = "info",
}: VenueDetailModalProps) {
	const [showMap, setShowMap] = useState(initialView === "map");

	// Reset map view when modal closes
	const handleClose = () => {
		setShowMap(initialView === "map");
		onClose();
	};

	// Build full address
	const addressParts = [];
	if (venue.address) addressParts.push(venue.address);
	if (venue.city) addressParts.push(venue.city);
	if (venue.state) addressParts.push(venue.state);
	if (venue.country) addressParts.push(venue.country);
	const fullAddress = addressParts.join(", ");

	// Include venue name for better place matching in Google Maps
	const mapQuery = venue.name ? `${venue.name}, ${fullAddress}` : fullAddress;
	const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;

	// Map view
	if (showMap) {
		return (
			<Dialog open={isOpen} onOpenChange={handleClose}>
				<DialogContent className="!max-w-4xl !p-4 !gap-0 overflow-hidden" showCloseButton={false}>
					<DialogHeader className="sr-only">
						<DialogTitle>{venue.name} Location</DialogTitle>
						<DialogDescription>Map showing {venue.name} location</DialogDescription>
					</DialogHeader>

					{/* Custom close button with circular background */}
					<DialogClose className="absolute right-6 top-6 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-white backdrop-blur-sm transition-colors hover:bg-black/90">
						<RiCloseLine className="h-5 w-5" />
						<span className="sr-only">Close</span>
					</DialogClose>

					<div className="overflow-hidden rounded-2xl aspect-video">
						<iframe
							src={mapUrl}
							width="100%"
							height="100%"
							style={{ border: 0 }}
							allowFullScreen
							loading="lazy"
							referrerPolicy="no-referrer-when-downgrade"
							title={`${venue.name} location map`}
						/>
					</div>
				</DialogContent>
			</Dialog>
		);
	}

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{venue.name}</DialogTitle>
					<DialogDescription className="sr-only">
						Venue details for {venue.name}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Address - Clickable */}
					{fullAddress && (
						<div className="flex items-start gap-3">
							<RiMapPinLine className="mt-0.5 h-5 w-5 text-muted-foreground" />
							<div>
								<p className="font-medium text-left text-sm">Address</p>
								<button
									type="button"
									onClick={() => setShowMap(true)}
									className="text-left text-primary text-sm leading-relaxed underline hover:text-primary/80"
								>
									{fullAddress}
								</button>
							</div>
						</div>
					)}

					{/* Capacity */}
					{venue.capacity !== null && venue.capacity !== undefined && (
						<div className="flex items-center gap-3">
							<RiTeamLine className="h-5 w-5 text-muted-foreground" />
							<div>
								<p className="font-medium text-left text-sm">Capacity</p>
								<p className="text-left text-muted-foreground text-sm">
									{venue.capacity.toLocaleString()}
								</p>
							</div>
						</div>
					)}

					{/* Venue Type */}
					{venue.venue_type && (
						<div className="flex items-center gap-3">
							{venue.venue_type === "indoor" ? (
								<RiDoorClosedLine className="h-5 w-5 text-muted-foreground" />
							) : venue.venue_type === "outdoor" ? (
								<RiSunLine className="h-5 w-5 text-muted-foreground" />
							) : (
								<RiTentLine className="h-5 w-5 text-muted-foreground" />
							)}
							<div>
								<p className="font-medium text-left text-sm">Type</p>
								<p className="text-left text-muted-foreground text-sm capitalize">
									{venue.venue_type.replace("_", " / ")}
								</p>
							</div>
						</div>
					)}
				</div>

				{showUpcomingShowsButton && (
					<DialogFooter className="mt-2">
						{venue.slug ? (
							<Button asChild className="w-full">
								<Link
									href={`/shows?venue=${venue.slug}`}
									onClick={onClose}
									className="inline-flex items-center gap-2"
								>
									<RiCalendarEventLine className="h-4 w-4" />
									<span>Show Upcoming Shows</span>
								</Link>
							</Button>
						) : (
							<Button disabled className="w-full">
								<RiCalendarEventLine className="h-4 w-4" />
								<span>Show Upcoming Shows</span>
							</Button>
						)}
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	);
}
